import { Box, Typography, Checkbox, FormControlLabel, Button, Radio, RadioGroup, MenuItem, TextField } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Upload } from 'antd';
import { ImageIcon, X } from 'lucide-react';
import CustomTextField from '../../components/FormElements/CustomTextField'
import UploadBadge from '../../components/UploadBadge'
import TimePresetButtons from '../../components/FormElements/TimePresetButtons'
import LadderField, {
    LADDER_MODE,
    ladderFieldsFromMode,
    ladderModeFromTrack,
    normalizeLadder,
} from '../../components/FormElements/LadderField'
import { DANCE_FORMATS, danceFormatFromPlayModes, playModesFromDanceFormat } from './danceHelpers'
import { MAX_STICKERS } from '../ManageChallenge/helpers'
import ThemeStickerPicker from '../../components/ThemeStickerPicker'
import FeedGroupSizeField, { normalizeFeedGroupSize } from '../../components/FormElements/FeedGroupSizeField'
import ThemeContextBanner from '../../components/FormElements/ThemeContextBanner'

const { Dragger } = Upload;

const themeStatusLabel = (theme) =>
    theme?.status === 'active' ? 'Active now' : 'Upcoming'

/** "Oct 27 – Nov 2, 2026" for a theme's window */
const themeWindowLabel = (theme) => {
    if (!theme?.startDate || !theme?.endDate) return ''
    return `${dayjs(theme.startDate).format('MMM D')} – ${dayjs(theme.endDate).format('MMM D, YYYY')}`
}

const DanceForm = ({
    setDanceForm,
    setIsEnabled,
    danceForm,
    stickers = [],
    onStickerUpload,
    onAddLibrarySticker,
    onRemoveSticker,
    onClearStickers,
    themeContext = null,
    // In-form theme picker (create only, when not opened from a theme):
    // active + upcoming themes, and the callback that selects one.
    themeOptions = null,
    onPickTheme,
    isEdit = false,
}) => {

    const [title, setTitle] = useState(danceForm?.title || "")
    // Player's choice (ladder = preselected) or hard-set Ranked / For fun.
    const [ladderMode, setLadderMode] = useState(ladderModeFromTrack(danceForm))
    const [ladder, setLadder] = useState(normalizeLadder(danceForm?.ladder))
    const [format, setFormat] = useState(
        danceForm?.format || danceFormatFromPlayModes(danceForm?.playModes)
    )
    const liveOnly = format === DANCE_FORMATS.LIVE_ONLY
    const [imageId, setImageId] = useState("")
    const [image, setImage] = useState(danceForm?.image || '')
    const [availabilityStartTime, setAvailabilityStartTime] = useState(
        danceForm?.availabilityStartTime ? dayjs(danceForm.availabilityStartTime) : null
    )
    const [availabilityEndTime, setAvailabilityEndTime] = useState(
        danceForm?.availabilityEndTime ? dayjs(danceForm.availabilityEndTime) : null
    )
    const [isUnscheduled, setIsUnscheduled] = useState(Boolean(danceForm?.isUnscheduled))
    const [datesLocked, setDatesLocked] = useState(
        Boolean(themeContext?.themeId) && !isEdit
    )
    const [feedGroupSize, setFeedGroupSize] = useState(
        normalizeFeedGroupSize(danceForm?.feedGroupSize)
    )

    // A theme picked in the form (not from the URL) arrives after mount, so
    // the dates above were already initialised. Re-sync them to the new
    // theme's window and lock them like the deep-link flow does.
    const lastThemeIdRef = useRef(themeContext?.themeId || '')
    useEffect(() => {
        const themeId = themeContext?.themeId || ''
        if (themeId === lastThemeIdRef.current) return
        lastThemeIdRef.current = themeId
        if (themeId) {
            setAvailabilityStartTime(themeContext.startDate ? dayjs(themeContext.startDate) : null)
            setAvailabilityEndTime(themeContext.endDate ? dayjs(themeContext.endDate) : null)
            setIsUnscheduled(false)
            setDatesLocked(!isEdit)
        } else {
            setDatesLocked(false)
        }
    }, [themeContext, isEdit])

    const canPickTheme = !isEdit && Array.isArray(themeOptions) && typeof onPickTheme === 'function'

    const imageUploadHandler = (data) => {
        setImageId(data?._id)
        setImage(data.url)
    }

    useEffect(() => {
        setDanceForm({
            title: title,
            coverImageId: imageId,
            image: image,
            availabilityStartTime: availabilityStartTime ? availabilityStartTime.toISOString() : null,
            availabilityEndTime: availabilityEndTime ? availabilityEndTime.toISOString() : null,
            isUnscheduled: isUnscheduled,
            ...(liveOnly
                ? { ladder: 'ranked', ladderLocked: true }
                : ladderFieldsFromMode(ladderMode, ladder)),
            format: format,
            playModes: playModesFromDanceFormat(format),
            feedGroupSize: feedGroupSize,
        })
        setIsEnabled(image && title)

    }, [imageId, title, image, availabilityStartTime, availabilityEndTime, isUnscheduled, ladder, ladderMode, format, liveOnly, feedGroupSize])


    return (
        <Box
            sx={{
                width: '60%',
                padding: '25px 20px',
                display: 'flex',
                flexDirection: 'column',
                marginTop: '10px',
            }}>
            <Box width={"100%"}>
                <CustomTextField
                    label="Title of the Dance Track"
                    onChange={e => setTitle(e.target.value)}
                    value={title}
                    inputProps={{
                        maxLength: 50,
                    }}
                />
            </Box>
            <Box mt={2}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px', marginBottom: '16px' }}>
                    Add Cover Image
                    <span style={{ fontSize: "10px", color: "gray", fontWeight: 400 }}>
                        (Upload an image and crop it to a square. Larger images recommended for best quality.)
                    </span>
                </Typography>
                <UploadBadge
                    selectedBadge={danceForm}
                    onImageUpload={imageUploadHandler}
                    karaoke={true}
                />
            </Box>

            <Box mt={3} width={"100%"}>
                <LadderField
                    mode={ladderMode}
                    onModeChange={setLadderMode}
                    preselected={ladder}
                    onPreselectedChange={setLadder}
                    liveOnly={liveOnly}
                    isEdit={isEdit}
                />
            </Box>
            <Box mt={3} width={"100%"}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                    Track format
                </Typography>
                <RadioGroup
                    value={format}
                    onChange={(e) => {
                        const next = e.target.value
                        setFormat(next)
                        if (next === DANCE_FORMATS.LIVE_ONLY) {
                            setLadder('ranked')
                            setLadderMode(LADDER_MODE.RANKED)
                        }
                    }}
                >
                    <FormControlLabel
                        value={DANCE_FORMATS.VIDEO_ONLY}
                        control={<Radio />}
                        label={
                            <Box>
                                <Typography sx={{ fontSize: '14px' }}>Video only</Typography>
                                <Typography sx={{ fontSize: '12px', color: 'gray' }}>
                                    Users submit recorded videos. The Dance Live schedule never affects this track.
                                </Typography>
                            </Box>
                        }
                        sx={{ alignItems: 'flex-start', mb: 1 }}
                    />
                    <FormControlLabel
                        value={DANCE_FORMATS.LIVE}
                        control={<Radio />}
                        label={
                            <Box>
                                <Typography sx={{ fontSize: '14px' }}>Live and video</Typography>
                                <Typography sx={{ fontSize: '12px', color: 'gray' }}>
                                    Users can record a video at any time, and battle live whenever a Dance Live window is open.
                                </Typography>
                            </Box>
                        }
                        sx={{ alignItems: 'flex-start', mb: 1 }}
                    />
                    <FormControlLabel
                        value={DANCE_FORMATS.LIVE_ONLY}
                        control={<Radio />}
                        label={
                            <Box>
                                <Typography sx={{ fontSize: '14px' }}>Live only</Typography>
                                <Typography sx={{ fontSize: '12px', color: 'gray' }}>
                                    Ranked live battles only, and only while a Dance Live window is open. There is no record button.
                                </Typography>
                            </Box>
                        }
                        sx={{ alignItems: 'flex-start' }}
                    />
                </RadioGroup>
            </Box>

            <Box mt={3} width={"100%"}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                    Stickers (up to {MAX_STICKERS})
                </Typography>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                >
                    Shown on the recording edit screen for this dance track. PNG
                    keeps transparency. Saving replaces the whole set.
                </Typography>
                {stickers.length > 0 && (
                    <Box className="sticker-preview-row">
                        {stickers.map((sticker, index) => (
                            <Box
                                key={sticker.mediaId || sticker.url}
                                className="contest-upload-preview contest-upload-preview--sticker"
                            >
                                <img src={sticker.url} alt={`sticker ${index + 1}`} />
                                <button
                                    className="contest-upload-remove"
                                    onClick={() => onRemoveSticker(index)}
                                    type="button"
                                >
                                    <X size={14} />
                                </button>
                            </Box>
                        ))}
                    </Box>
                )}
                {stickers.length < MAX_STICKERS && (
                    <Box sx={{ mt: stickers.length ? 1.5 : 0 }}>
                        <Dragger
                            multiple
                            showUploadList={false}
                            customRequest={onStickerUpload}
                            accept="image/*"
                            className="contest-upload-dragger"
                        >
                            <Box className="contest-upload-dragger-content">
                                <ImageIcon size={32} strokeWidth={1.5} color="#667eea" />
                                <Typography
                                    variant="body2"
                                    sx={{ mt: 1, fontWeight: 500 }}
                                >
                                    Drag & drop stickers here
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    or click to browse ({stickers.length} of{" "}
                                    {MAX_STICKERS} added, PNG/JPG, max 5MB)
                                </Typography>
                            </Box>
                        </Dragger>
                    </Box>
                )}
                <ThemeStickerPicker
                    stickers={stickers}
                    maxStickers={MAX_STICKERS}
                    onPick={onAddLibrarySticker}
                />
                {stickers.length > 0 && (
                    <Button
                        size="small"
                        onClick={onClearStickers}
                        sx={{
                            mt: 1,
                            textTransform: 'none',
                            color: '#d32f2f',
                            padding: 0,
                            minWidth: 0,
                        }}
                    >
                        Clear all stickers
                    </Button>
                )}
            </Box>

            <FeedGroupSizeField
                value={feedGroupSize}
                onChange={setFeedGroupSize}
                defaultLabel="Default (3)"
                hint="How many pagents of this track appear back-to-back on the voting feed."
            />
            <Box mt={3} width={"100%"}>
                <Typography sx={{ fontWeight: 600, fontSize: '14px', marginBottom: '16px' }}>
                    Availability Period
                </Typography>
                {canPickTheme && (
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            select
                            fullWidth
                            label="Theme this dance is for"
                            value={themeContext?.themeId || ''}
                            onChange={(e) => onPickTheme(e.target.value)}
                            SelectProps={{
                                renderValue: (selected) => {
                                    const theme = themeOptions.find((option) => String(option._id) === selected)
                                    return theme
                                        ? `${theme.title} (${themeStatusLabel(theme)} · ${themeWindowLabel(theme)})`
                                        : themeContext?.title || ''
                                },
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '6px' },
                                '& .MuiInputBase-input': { color: 'black !important' },
                            }}
                        >
                            <MenuItem value="">No theme — set the dates below</MenuItem>
                            {themeOptions.map((theme) => (
                                <MenuItem key={theme._id} value={String(theme._id)}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                        <span>{theme.title}</span>
                                        <span style={{ fontSize: '11px', color: 'gray' }}>
                                            {themeStatusLabel(theme)} · {themeWindowLabel(theme)}
                                        </span>
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {themeOptions.length === 0
                                ? 'No active or upcoming themes right now.'
                                : 'Pick the theme to fill in its start and end dates and link the dance to it.'}
                        </Typography>
                    </Box>
                )}
                {!isEdit && (
                    <ThemeContextBanner
                        themeContext={themeContext}
                        kind="dance"
                        datesLocked={datesLocked}
                        onUnlock={() => setDatesLocked(false)}
                    />
                )}
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isUnscheduled}
                            disabled={datesLocked}
                            onChange={(e) => {
                                setIsUnscheduled(e.target.checked)
                                if (e.target.checked) {
                                    setAvailabilityStartTime(null)
                                    setAvailabilityEndTime(null)
                                }
                            }}
                        />
                    }
                    label={
                        <span style={{ fontSize: '14px' }}>
                            Release date not decided yet (TBD)
                            <span style={{ fontSize: '11px', color: 'gray', display: 'block' }}>
                                Track stays hidden from users until it's scheduled
                            </span>
                        </span>
                    }
                    sx={{ mb: 1 }}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box>
                            <DateTimePicker
                                label="Start Date & Time"
                                value={availabilityStartTime}
                                onChange={(newValue) => setAvailabilityStartTime(newValue)}
                                ampm={true}
                                format="MM/DD/YYYY hh:mm A"
                                disabled={isUnscheduled || datesLocked}
                                sx={{
                                    width: '100%',
                                    '& .MuiInputBase-input': {
                                        color: 'black !important',
                                    },
                                }}
                            />
                            {!isUnscheduled && !datesLocked && (
                                <TimePresetButtons value={availabilityStartTime} onChange={setAvailabilityStartTime} />
                            )}
                        </Box>
                        <Box>
                            <DateTimePicker
                                label="End Date & Time"
                                value={availabilityEndTime}
                                onChange={(newValue) => setAvailabilityEndTime(newValue)}
                                ampm={true}
                                format="MM/DD/YYYY hh:mm A"
                                minDateTime={availabilityStartTime || undefined}
                                disabled={isUnscheduled || datesLocked}
                                sx={{
                                    width: '100%',
                                    '& .MuiInputBase-input': {
                                        color: 'black !important',
                                    },
                                }}
                            />
                            {!isUnscheduled && !datesLocked && (
                                <TimePresetButtons value={availabilityEndTime} onChange={setAvailabilityEndTime} />
                            )}
                        </Box>
                    </Box>
                </LocalizationProvider>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                    Optional: Set when this dance track will be available to users
                </Typography>
            </Box>
        </Box>
    )
}

export default DanceForm
